/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import { switchMap, map, catchError, of, filter, forkJoin, debounceTime, distinctUntilChanged, EMPTY, concatMap } from "rxjs";
import { Epic } from "redux-observable";

import { Action, ActionType } from "lib/ActionType";

import ServicesContext from "services/ServicesContext";

import * as slice from "./availableAssetFilters.slice";


type ActionsType = ActionType<typeof slice.actions>;
type TEpic<I extends Action<any> = any, O extends I & ActionsType = any> = Epic<I, O, { availableAssetFilters: slice.AvailableAssetFiltersState }, ServicesContext>;


export function _humanizeVendorsProducts(vp: string[]): string[] {

   const fixedVP = vp.map(
      v => v.toLocaleLowerCase()
         .replaceAll("_", " ")
         .replace(/\w\S*/g, m => m.charAt(0).toUpperCase() + m.substr(1).toLocaleLowerCase())
   );

   return fixedVP;

}

export function _fixVendorProduct(vp: string) {
   return vp.replaceAll(" ", "_").toLocaleLowerCase();
}


const selectVendor: TEpic<ActionType<typeof slice.actions.selectVendor>> = (action$, state, deps) => {

   return action$.pipe(
      filter(
         action => action.type === slice.actions.selectVendor.type
      ),
      distinctUntilChanged((prev, next) => prev.payload.vendor === next.payload.vendor),
      switchMap(
         (action) => {

            if (
               _fixVendorProduct(state.value.availableAssetFilters.selectedVendor) !==
               _fixVendorProduct(action.payload.vendor)
            ) {
               return EMPTY;
            }

            const actions: any = [
               slice.actions.clearProducts(),
               slice.actions.selectProduct({ product: "" }),
               slice.actions.loadVendors({})
            ]

            if (action.payload.vendor) actions.push(
               slice.actions.loadProducts({})
            );

            return of(...actions);
         }
      )
   )

}


const selectProduct: TEpic<ActionType<typeof slice.actions.selectProduct>> = (action$, state, deps) => {

   return action$.pipe(
      filter(
         action => action.type === slice.actions.selectProduct.type
      ),
      distinctUntilChanged((prev, next) => prev.payload.product === next.payload.product),
      switchMap(
         action => {

            const actions: any[] = [];

            actions.push(slice.actions.setSelectedProduct({ product: action.payload.product }))

            if (action.payload.product) {

               if (
                  _fixVendorProduct(state.value.availableAssetFilters.selectedProduct) !==
                  _fixVendorProduct(action.payload.product.toLocaleLowerCase())
               ) {
                  actions.push(slice.actions.loadProducts({}))
               }

               if (
                  state.value.availableAssetFilters.products.findIndex(p => p.toLocaleLowerCase() === action.payload.product.toLocaleLowerCase()) !== -1
               ) {

                  if (
                     _fixVendorProduct(state.value.availableAssetFilters.selectedVendor) !== _fixVendorProduct(state.value.availableAssetFilters.availableFiltersLoadedFor.vendor) ||
                     _fixVendorProduct(state.value.availableAssetFilters.selectedProduct) !== _fixVendorProduct(state.value.availableAssetFilters.availableFiltersLoadedFor.product)
                  ) {
                     actions.push(slice.actions.loadAvailableFilters());
                  }

               } else {
                  actions.push(slice.actions.clearAvailableFilters());
               }

            } else {

               if (state.value.availableAssetFilters.selectedVendor) {
                  actions.push(slice.actions.loadProducts({}));
               }

               actions.push(slice.actions.clearAvailableFilters());

            }

            actions.push(slice.actions.setSelectedProduct({ product: action.payload.product }))

            return of(...actions);

         }
      )
   )

}


const loadVendors: TEpic<ActionType<typeof slice.actions.loadVendors>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.loadVendors.type
      ),
      debounceTime(200),
      switchMap(
         action => {
            return deps.api.cpeDataProvider.listVendors(
               _fixVendorProduct(state.value.availableAssetFilters.selectedVendor),
               undefined,
               action.payload.count
            ).pipe(
               concatMap(
                  vendors => {
                     if (state.value.availableAssetFilters.scheduledProductToSelect) {
                        return of(
                           slice.actions.selectProduct({ product: state.value.availableAssetFilters.scheduledProductToSelect }),
                           slice.actions.vendorsLoaded({ vendors: _humanizeVendorsProducts(vendors) }),
                        )
                     } else {
                        return of(slice.actions.vendorsLoaded({ vendors: _humanizeVendorsProducts(vendors) }))
                     }
                  }
               ),
               catchError(err => of(slice.actions.vendorsLoadingError({ err })))
            )
         }
      )
   )

}


const loadNextVendors: TEpic<ActionType<typeof slice.actions.loadNextVendors>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.loadNextVendors.type
      ),
      debounceTime(200),
      switchMap(
         action => {
            return deps.api.cpeDataProvider.listVendors(
               _fixVendorProduct(state.value.availableAssetFilters.selectedVendor),
               state.value.availableAssetFilters.vendors.length > 0 ?
                  _fixVendorProduct(state.value.availableAssetFilters.vendors[state.value.availableAssetFilters.vendors.length - 1])
               :
                  undefined,
               action.payload.count
            ).pipe(
               map(vendors => slice.actions.nextVendorsLoaded({nextVendors: _humanizeVendorsProducts(vendors)})),
               catchError(err => of(slice.actions.nextVendorsLoadingError({ err })))
            )
         }
      )
   )

}


const loadProducts: TEpic<ActionType<typeof slice.actions.loadProducts>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.loadProducts.type
      ),
      debounceTime(200),
      switchMap(
         action => {

            if (!state.value.availableAssetFilters.selectedVendor) {
               return of(
                  slice.actions.selectProduct({ product: "" }),
                  slice.actions.productsLoaded({ products: [] })
               )
            }

            return deps.api.cpeDataProvider.listProducts(
               _fixVendorProduct(state.value.availableAssetFilters.selectedVendor),
               _fixVendorProduct(state.value.availableAssetFilters.selectedProduct) || undefined,
               undefined,
               action.payload.count
            ).pipe(
               map(products => slice.actions.productsLoaded({ products: _humanizeVendorsProducts(products) })),
               catchError(err => of(slice.actions.productsLoadingError({ err })))
            )
         }
      )
   )

}


const loadNextProducts: TEpic<ActionType<typeof slice.actions.loadNextProducts>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.loadNextProducts.type
      ),
      debounceTime(200),
      switchMap(
         action => { return deps.api.cpeDataProvider.listProducts(
            _fixVendorProduct(state.value.availableAssetFilters.selectedVendor),
            _fixVendorProduct(state.value.availableAssetFilters.selectedProduct),
            state.value.availableAssetFilters.products.length > 0 ?
               _fixVendorProduct(state.value.availableAssetFilters.products[state.value.availableAssetFilters.products.length - 1])
            :
               undefined,
            action.payload.count
         ).pipe(
            map(products => slice.actions.nextProductsLoaded({ nextProducts: _humanizeVendorsProducts(products) })),
            catchError(err => of(slice.actions.nextProductsLoadingError({ err })))
         )}
      )
   )

}


const loadAvailableFiltersOnVendorProductSelection: TEpic<ActionType<typeof slice.actions.selectProduct>> = (action$, state, deps) => {

   return action$.pipe(
      filter(
         action =>
            action.type === slice.actions.vendorsLoaded.type ||
            action.type === slice.actions.productsLoaded.type
      ),
      map(
         () => {

            if (
               _fixVendorProduct(state.value.availableAssetFilters.selectedVendor) === _fixVendorProduct(state.value.availableAssetFilters.availableFiltersLoadedFor.vendor) &&
               _fixVendorProduct(state.value.availableAssetFilters.selectedProduct) === _fixVendorProduct(state.value.availableAssetFilters.availableFiltersLoadedFor.product)
            ) {
               return { type: "" };
            }

            if (
               state.value.availableAssetFilters.vendors.findIndex(a => _fixVendorProduct(a) === _fixVendorProduct(state.value.availableAssetFilters.selectedVendor)) !== -1 &&
               state.value.availableAssetFilters.products.findIndex(a => _fixVendorProduct(a) === _fixVendorProduct(state.value.availableAssetFilters.selectedProduct)) !== -1
            ) {
               return slice.actions.loadAvailableFilters()
            } else {
               return slice.actions.clearAvailableFilters()
            }

         }
      )
   )

}


const loadAvailableFilters: TEpic<ActionType<typeof slice.actions.loadAvailableFilters>> = (action$, state, deps) => {

   return action$.pipe(
      filter(
         action => action.type === slice.actions.loadAvailableFilters.type
      ),
      switchMap(
         () => {

            const vendor = _fixVendorProduct(state.value.availableAssetFilters.selectedVendor);
            const product = _fixVendorProduct(state.value.availableAssetFilters.selectedProduct)

            return forkJoin([
               deps.api.cpeDataProvider.listCpePart(vendor, product, "part", undefined, undefined, 10000),
               deps.api.cpeDataProvider.listCpePart(vendor, product, "version", undefined, undefined, 10000),
               deps.api.cpeDataProvider.listCpePart(vendor, product, "update", undefined, undefined, 10000),
               deps.api.cpeDataProvider.listCpePart(vendor, product, "edition", undefined, undefined, 10000),
               deps.api.cpeDataProvider.listCpePart(vendor, product, "language", undefined, undefined, 10000),
               deps.api.cpeDataProvider.listCpePart(vendor, product, "sw_edition", undefined, undefined, 10000),
               deps.api.cpeDataProvider.listCpePart(vendor, product, "target_sw", undefined, undefined, 10000),
               deps.api.cpeDataProvider.listCpePart(vendor, product, "target_hw", undefined, undefined, 10000),
               deps.api.cpeDataProvider.listCpePart(vendor, product, "other", undefined, undefined, 10000),
            ]).pipe(
               map(parts => slice.actions.availableFiltersLoaded({ parts })),
               catchError(err => of(slice.actions.availableFiltersLoadingError({ err })))
            )

         }
      )
   )

}



const epics: any = [
   selectVendor,
   selectProduct,
   loadVendors,
   loadNextVendors,
   loadProducts,
   loadNextProducts,
   loadAvailableFilters,
   loadAvailableFiltersOnVendorProductSelection
];

export default epics;